import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators'
import store from '@app/componentlib/src/store'
import { apiUrl } from '@app/componentlib/src/application/helpers/api'
import { httpService, Method } from '@app/componentlib/src/services/HttpService'
import Vue from 'vue'
import { profileExclusionParambuilder } from '@app/componentlib/src/application/helpers/CvToolHelper'
import { DisplayItem } from '@app/componentlib/src/common/types/profileTypes'
import { HelpTextProfile, CV, ICVState, CvId, ExcludeItem, GetCVRequest, CVMotivationalTexts } from '@app/componentlib/common/types/CVTypes'

const documentApi = 'DocumentApi'

@Module({
  dynamic: true,
  store,
  name: 'cvdata',
  namespaced: true
})
class CvData extends VuexModule implements ICVState {
  _initialized = false
  _cvId: CvId | undefined
  _excludePreviewList: ExcludeItem[] = []
  _previewUrls: string[] = []
  _previewUrl: string |undefined = undefined
  _colorTheme: number | undefined = undefined
  _selectedProfileId: number | null = null
  _pageCount: number | undefined
  _helpTextId: number | undefined = undefined
  _helptextProfiles: HelpTextProfile[] = []
  _savingToDocuments: boolean = false
  _savingCoverLetterToDocuments: boolean = false
  _selectedCv: CV | undefined = undefined
  _allCvs: CV[] | undefined = undefined
  _helpTextProfile: HelpTextProfile | undefined = undefined

  _useMotivationalTexts: boolean = false
  _motivationalHeadline: string = ""
  _motivationalText: string = ""

  @Action({ rawError: true })
  async init () {
    const tempCvId = localStorage.getItem('cvId')
    if(tempCvId !== null) {
      const cvId: CvId = JSON.parse(tempCvId)
      this.context.commit('SET_CVID', cvId)
      const excludePreviewList = localStorage.getItem(cvId.mediaId.toString())
      if(excludePreviewList !== null) {
        this.context.commit('SET_EXCLUDEPREVIEWLIST', JSON.parse(excludePreviewList))
      }
    }
    const profileIdStr = localStorage.getItem('selectedProfileId')
    let tempProfileId  = null
    if(profileIdStr !== null) tempProfileId = parseInt(profileIdStr)
    this.context.commit('SET_SELECTEDPROFILE', tempProfileId)
    this.context.commit('SET_COLORTHEME')
    console.log('CVDATA: Initialized')
    this.context.commit('SET_INITIALIZED', true)
    this.getMotivationalTexts()
  }

  @Mutation
  RESET () {
    this._selectedProfileId = null
    this._colorTheme = undefined
  }

  @Action({ rawError: true })
  async setCvId (CvId: CvId) {
    console.log('CVDATA: Set CV ID', CvId)
    const cvIdStr = JSON.stringify(CvId)
    localStorage.setItem('cvId', cvIdStr)
    this.context.commit('SET_CVID', CvId)
  }

  @Action({ rawError: true })
  async setCvLang (langCode: string) {
    //Remove previews to get new ones, when changing language
    CvModule.removePreviewUrls()
    console.log('CVDATA: Set CV LANGUAGE', langCode)
    const cvId = {
      mediaId: this.cvId?.mediaId,
      lang: langCode
    }
    const cvIdStr = JSON.stringify(cvId)
    localStorage.setItem('cvId', cvIdStr)
    this.context.commit('SET_CVID', cvId)
    //when lang changes remove helpTextId and the selected profile (As we want one in another language)
    this.setHelpTextProfile()
    this.setHelpTexts()

    // When changing language, we want to change the selected CV to a affiliated CV with the new language
    const newSelectedCv = this.affiliatedDocuments?.find((Cv: CV) => Cv.language === langCode)
    if(newSelectedCv !== undefined) this.setSelectedCv(newSelectedCv)
  }

  @Action({ rawError: true })
  async removeCvId () {
    localStorage.removeItem('cvId')
    this.context.commit('SET_CVID', undefined)
  }

  @Action({ rawError: true })
  async excludeItem (item: DisplayItem) {
    //Remove previews to get new ones, when excluding item
    CvModule.removePreviewUrls()
    console.log('CVDATA: Exclude item in preview', item)
    if(this._excludePreviewList !== undefined) {
      const list = this._excludePreviewList
      if(list.find(i => i.id === item.Id) === undefined ) {
        list.push({id: item.Id, type: item.type})
        this.context.commit('SET_EXCLUDEPREVIEWLIST', list)
      }
    }
  }

  @Action({ rawError: true })
  async includeItem (item: DisplayItem) {
    //Remove previews to get new ones, when including item
    CvModule.removePreviewUrls()
    console.log('CVDATA: Include item in preview', item)
    if(this._excludePreviewList !== undefined) {
      const list = this._excludePreviewList
      if(list.find(i => i.id === item.Id) !== undefined) {
        //remove id from list
        this.context.commit('SET_EXCLUDEPREVIEWLIST', list.filter(list => list.id !== item.Id))
      }
    }
  }

  @Action({ rawError: true })
  async getCV (requestParameters: GetCVRequest) {
    console.log('getcv request params:', requestParameters)
    const endpoint = requestParameters.template === true ?
                     "GetCV" : "GetExampleCV"
    const r = { endpoint: endpoint, req: requestParameters}
    let requestUrl = await this.cvRequestUrl(r)

    console.log('PROFILE: Get CV', requestUrl)
    window.open(requestUrl)
  }

  @Action({ rawError: true })
  async getCoverLetter (requestParameters: GetCVRequest) {
    console.log('getCoverLetter request params:', requestParameters)
    let requestUrl = apiUrl(documentApi, 'GetCoverLetter', requestParameters)
    console.log('PROFILE: Get Cover Letter', requestUrl)
    window.open(requestUrl)
  }

  @Action({ rawError: true })
  async setCVPreview (requestParameters: GetCVRequest) {
    console.log('getcv request params:', requestParameters)
    const endpoint = requestParameters.template === true ?
                     "GetCV" : "GetExampleCV"
    let requestUrl = apiUrl(documentApi, endpoint, requestParameters)

    let profileExclusions: string = ''
    if(this.excludePreviewList !== undefined) {
      profileExclusions = profileExclusionParambuilder(this.excludePreviewList)
    }
    if(profileExclusions.length !== 0) requestUrl = requestUrl + '&' + profileExclusions

    this.context.commit('SET_PREVIEW_URL', requestUrl)
  }

  @Action ({ rawError: true })
  async setUseMotivationalTexts(bool: boolean) {
    this.context.commit('SET_USEMOTIVATIONALTEXTS', bool)
  }

  @Action({ rawError: true })
  async GetMissingProfileSections(): Promise<string[] | undefined> {
    const mediaId = this.cvId?.mediaId
    if(mediaId !== undefined) {
      let url = apiUrl(documentApi, `GetMissingProfileSections`, {mediaId: mediaId})
      let profileExclusions: string = ''
      if(this.excludePreviewList !== undefined) {
        profileExclusions = profileExclusionParambuilder(this.excludePreviewList)
      }
      if(profileExclusions.length !== 0) url = url + '&' + profileExclusions

      const result = await httpService.get(url)
      return result
    }
    return undefined
  }

  @Action({ rawError: true })
  async GetCVSections(): Promise<string[] | undefined> {
    const mediaId = this.cvId?.mediaId
    if(mediaId !== undefined) {
      let url = apiUrl(documentApi, `GetCVSections`, {mediaId: mediaId})
      const result = await httpService.get(url)
      return result
    }
    return undefined
  }

  @Action({ rawError: true })
  async changeColor (id: number) {
    //Remove previews to get new ones, when changing color
    CvModule.removePreviewUrls()
    this.context.commit('SET_COLORTHEME', id)
  }

  @Action({ rawError: true })
  changeSelectedProfile (id?: number) {
    this.context.commit('SET_SELECTEDPROFILE', id)
  }

  @Action({ rawError: true })
  async saveToDocuments (requestParameters: GetCVRequest) {
    if(this.savingToDocuments !== true) {
      this.context.commit('SET_SAVINGTODOCUMENTS', true)
      let endpoint = 'SaveCVToDocuments'
      const r = { endpoint: endpoint, req: requestParameters}
      let requestUrl = await this.cvRequestUrl(r)

      await httpService.post(requestUrl, undefined)
      this.context.commit('SET_SAVINGTODOCUMENTS', false)
    } else {
      console.warn('Wait for the current download to finish!')
    }
  }

  @Action({ rawError: true })
  async saveCoverLetterToDocuments (requestParameters: GetCVRequest) {
    if(this.savingToDocuments !== true) {
      this.context.commit('SET_SAVINGCOVERLETTERTODOCUMENTS', true)
      const method: Method = Method.POST
      const url = apiUrl(documentApi, 'SaveCoverLetterToDocuments')
      await httpService._postParams(url, requestParameters)
      this.context.commit('SET_SAVINGCOVERLETTERTODOCUMENTS', false)
    } else {
      console.warn('Wait for the current download to finish!')
    }
  }

  @Action({ rawError: true })
  removeColorTheme () {
    localStorage.removeItem('selectedColorTheme')
    this.context.commit('SET_COLORTHEME')
  }

  @Action({ rawError: true })
  async clearCVSelections () {
    this.removePreviewUrls()
    this.removeCvId()
    this.removeColorTheme()
    //Clear selected profile, by not sending a value
    this.context.commit('SET_SELECTEDPROFILE')
  }

  @Action({ rawError: true })
  async getPageCount () {
    //GetCVPageCount
    //(string langCode = null, int? mediaId = null, int? profileNodeId = null, bool isTemplate = true, [FromUri] CVSettings settings = null)
    //Get PageCount and save it
    let requestUrl = apiUrl(documentApi, 'GetCVPageCount')
    let profileExclusions: string = ''
    if(this.excludePreviewList !== undefined) {
      profileExclusions = profileExclusionParambuilder(this.excludePreviewList)
    }
    requestUrl = requestUrl + '?mediaId='+ this.cvId?.mediaId
    if(profileExclusions.length !== 0) requestUrl = requestUrl + '&' + profileExclusions
    if(this.selectedProfileId !== undefined) requestUrl = requestUrl + '&profileNodeId=' + this.selectedProfileId

    const pageCount = await httpService.get(requestUrl)
    this.context.commit("SET_PAGECOUNT", pageCount)
  }

  @Action({ rawError: true })
  async removePreviewUrls () {
    this.context.commit('SET_PREVIEW_URL')
  }

  @Action({ rawError: true })
  setHelpTexts (helptextId?: number) {
    //Remove previews to get new ones, when using help texts
    CvModule.removePreviewUrls()
    if(helptextId !== undefined) {
      this.context.commit('SET_HELP_TEXTS', helptextId)
    } else {
      this.context.commit('SET_HELP_TEXTS')
    }
  }
  @Action({ rawError: true })
  setHelpTextProfiles (helptextProfiles: HelpTextProfile[]) {
    this.context.commit('SET_HELPTEXT_PROFILES', helptextProfiles)
  }

  @Action({ rawError: true })
  setSelectedCv (cv: CV) {
    this.context.commit('SET_SELECTED_CV', cv)
  }

  @Action({ rawError: true })
  deleteSelectedCv () {
    this.context.commit('SET_SELECTED_CV')
  }

  @Action({ rawError: true })
  setAllCvs (Cvs: CV[]) {
    this.context.commit('SET_ALL_CVS', Cvs)
  }
  @Action({ rawError: true})
  setHelpTextProfile () {
    const helpTextProfile = CvModule.helpTextProfiles.find(p => p.language === this.langCode)
    this.context.commit('SET_HELPTEXTPROFILE', helpTextProfile)
  }

  @Action({ rawError: true })
  setMotivationalHeadline(headline: string) {
    this.context.commit('SET_MOTIVATIONALHEADLINE', headline)
  }

  @Action({ rawError: true })
  setMotivationalText(text: string) {
    this.context.commit('SET_MOTIVATIONALTEXT', text)
  }

  @Action({ rawError: true })
  async saveMotivationalTexts(mt: CVMotivationalTexts) {
    const url = apiUrl(documentApi, 'SaveMotivationalText')
    await httpService.post(url, mt).then((data: any) => {
      if(data.ok === true) {
        this.setMotivationalText(mt.MotivationalText)
        this.setMotivationalHeadline(mt.MotivationalHeadline)
      }
    })
  }

  @Action({ rawError: true })
  async getMotivationalTexts() {
    const url = apiUrl(documentApi, 'GetMotivationalText')
    await httpService.get(url).then((data: any) => {
      if(data !== null) {
        this.setMotivationalHeadline(data.MotivationalHeadline)
        this.setMotivationalText(data.MotivationalText)
      }
    })
  }

  @Action({ rawError: true })
  async cvRequestUrl(r: {endpoint: string, req: GetCVRequest}) {
    // Create a request parameter used in several methodcvRequestUrls
    let requestUrl = apiUrl(documentApi, r.endpoint, r.req)
    if(this.useMotivationalTexts === true) {
      requestUrl = requestUrl + '&UseMotivationalTexts=' + this.useMotivationalTexts
    }

    let profileExclusions: string = ''
    if(this.excludePreviewList !== undefined) {
      profileExclusions = profileExclusionParambuilder(this.excludePreviewList)
    }
    if(profileExclusions.length !== 0) {
      requestUrl = requestUrl + '&' + profileExclusions
    }

    return requestUrl
  }

  // MUTATIONS
  @Mutation
  SET_INITIALIZED (val: boolean) {
    this._initialized = val
  }
  @Mutation
  SET_CVID (cvId: CvId) {
    Vue.set(this, '_cvId', cvId)
  }
  @Mutation
  SET_PREVIEW_URL (url?: string) {
    this._previewUrl = url
    if(url !== undefined) {
      //Only add urls that isnt in the list
      if(this._previewUrls.includes(url) === false) {
        this._previewUrls.push(url)
      }
    } else {
      Vue.set(this, '_previewUrls', [])
    }
  }
  @Mutation
  SET_EXCLUDEPREVIEWLIST (list: ExcludeItem[]) {
    if(this._cvId !== undefined) {
      localStorage.setItem(this._cvId.mediaId.toString(), JSON.stringify(list))
      this._excludePreviewList = list
    }
  }
  @Mutation
  SET_COLORTHEME (id?: number) {
    let colorId = undefined
    if(id !== undefined) {
      localStorage.setItem('selectedColorTheme', id.toString())
      colorId = id
    } else {
      const colorIdStr = localStorage.getItem('selectedColorTheme')
      if(colorIdStr) colorId = parseInt(colorIdStr)
    }
    Vue.set(this, '_colorTheme', colorId)
  }
  @Mutation
  SET_SELECTEDPROFILE (profileId?: number) {
    let selectedProfileId = null
    if(profileId !== null && profileId !== undefined) {
      localStorage.setItem('selectedProfileId', profileId.toString())
      selectedProfileId = profileId
    } else {
      localStorage.removeItem('selectedProfileId')
    }
    this._selectedProfileId = selectedProfileId
  }
  @Mutation
  SET_PAGECOUNT (pageCount?: number) {
    Vue.set(this, '_pageCount', pageCount)
  }
  @Mutation
  SET_HELP_TEXTS (helpTextId?: number) {
    this._helpTextId = helpTextId
  }
  @Mutation
  SET_HELPTEXT_PROFILES (helptextprofiles: HelpTextProfile[]) {
    Vue.set(this, '_helptextProfiles', helptextprofiles)
  }
  @Mutation
  SET_SAVINGTODOCUMENTS (bool: boolean) {
    Vue.set(this, '_savingToDocuments', bool)
  }
  @Mutation
  SET_SAVINGCOVERLETTERTODOCUMENTS (bool: boolean) {
    Vue.set(this, '_savingCoverLetterToDocuments', bool)
  }
  @Mutation
  SET_SELECTED_CV (cv?: CV) {
    Vue.set(this, '_selectedCv', cv)
  }
  @Mutation
  SET_ALL_CVS (cvs: CV[]) {
    Vue.set(this, '_allCvs', cvs)
  }
  @Mutation
  SET_HELPTEXTPROFILE (helpTextProfile: HelpTextProfile) {
    Vue.set(this, '_helpTextProfile', helpTextProfile)
  }
  @Mutation
  SET_MOTIVATIONALHEADLINE(headline: string) {
    Vue.set(this, '_motivationalHeadline', headline)
  }
  @Mutation
  SET_MOTIVATIONALTEXT(text: string) {
    Vue.set(this, '_motivationalText', text)
  }
  @Mutation
  SET_USEMOTIVATIONALTEXTS(bool: boolean) {
    Vue.set(this, '_useMotivationalTexts', bool)
  }


  //Getters
  get initialized (): boolean {
    return this._initialized
  }
  get cvId (): CvId | undefined {
    return this._cvId
  }
  get excludePreviewList (): ExcludeItem[] | undefined {
    return this._excludePreviewList
  }
  get previewUrls (): string[] {
    return this._previewUrls
  }
  get previewUrl (): string | undefined {
    return this._previewUrl
  }
  get colorTheme(): number | undefined {
    return this._colorTheme
  }
  get selectedProfileId (): number | null {
    return this._selectedProfileId
  }
  get langCode (): string | undefined {
    return this._cvId?.lang
  }
  get pageCount (): number | undefined {
    return this._pageCount
  }
  get helpTextId (): number | undefined {
    return this._helpTextId
  }
  get helpTextProfiles (): HelpTextProfile[] {
    return this._helptextProfiles
  }
  get savingToDocuments (): boolean {
    return this._savingToDocuments
  }
  get savingCoverLetterToDocuments (): boolean {
    return this._savingCoverLetterToDocuments
  }
  get selectedCv (): CV | undefined {
    return this._selectedCv
  }
  get affiliatedDocuments (): CV[] | undefined {
    if(this.selectedCv !== undefined && this.allCvs !== undefined) {
      const selLang = this.selectedCv.language
      const tempId = this.selectedCv.templateId
      return this.allCvs.filter((doc: CV) => doc.templateId === tempId && doc.language !== selLang)
    }
    return undefined
  }
  get allCvs (): CV[] | undefined {
    return this._allCvs
  }

  get helpTextProfile (): HelpTextProfile | undefined {
    return this._helpTextProfile
  }

  get motivationalHeadline (): string {
    return this._motivationalHeadline
  }

  get motivationalText (): string {
    return this._motivationalText
  }

  get useMotivationalTexts (): boolean {
    return this._useMotivationalTexts
  }
}

export const CvModule = getModule(CvData)